import { data as json, redirect } from "react-router"

import { url } from "@/lib/url"
import { publicFetcher } from "@/src/graphql/fetcher"
import { useCurrentUserQuery } from "@/src/graphql/public-generated-rq"
import { queryClient } from "@/src/lib/queryClient"

// To be used in the loader function of a page component
// Fetches the data for a query and returns it
export async function loaderQuery<
  TData extends Record<string, any>,
  TVars extends Record<string, any>,
  Query extends {
    getKey: (v: TVars) => (string | TVars)[]
    fetcher: (variables: TVars) => ReturnType<typeof publicFetcher<TData, TVars>>
  },
>(query: Query, variables: TVars): Promise<TData> {
  if (Object.keys(variables).length === 0) {
    variables = undefined as unknown as TVars
  }
  const queryKey = query.getKey(variables as TVars)
  const queryFn = query.fetcher(variables as TVars)

  const state = queryClient.getQueryState(queryKey)
  const data = queryClient.getQueryData<TData>(queryKey)
  // If the query is not in the cache or is invalidated, refetch
  if (!state || state.isInvalidated || !data) {
    return await queryClient.fetchQuery({ queryKey, queryFn })
  }

  return data
}

/**
 * Loader guard for admin-only destinations. Fetches the current user and
 * bounces anyone who shouldn't see the admin area: unauthenticated users to
 * /login, non-admins to their org workspace (or a 401 when they have no org).
 * Returns the current-user data for admins so callers can reuse it. Deciding
 * here — rather than redirecting to /admin first and checking there — keeps
 * non-admins off the admin-gated /admin URL entirely.
 */
export async function requireAdmin() {
  const data = await loaderQuery(useCurrentUserQuery, {})

  if (data.currentUser.pid == null)
    throw redirect("/login?next=" + window.location.href)

  if (!data.currentUser.isAdmin) {
    const orgSlug = data.currentUser.org?.slug
    if (orgSlug) throw redirect(url(["/[orgSlug]", { orgSlug }]))
    throw json({ message: "Unauthorized" }, { status: 401 })
  }

  return data
}

/**
 * Resolve a `?next=` param to a safe, same-origin path to redirect to, or null
 * when it is missing, malformed, cross-origin, or points back at /login.
 *
 * `next` is typically an absolute same-origin URL (requireAdmin sets it from
 * `window.location.href`), but it is attacker-controllable, so we only honor
 * same-origin targets to avoid an open redirect, and strip the origin so the
 * caller redirects to a relative path.
 */
export function resolveSafeNextPath(next: string | null | undefined): string | null {
  if (!next) return null
  try {
    const target = new URL(next, window.location.origin)
    if (target.origin !== window.location.origin) return null
    // React Router matches routes case-insensitively and treats trailing
    // slashes as equivalent, so normalize both away before rejecting /login to
    // avoid bouncing an authenticated user back through the login route.
    const normalizedPath = target.pathname.replace(/\/+$/, "").toLowerCase()
    if (normalizedPath === "/login") return null
    return target.pathname + target.search + target.hash
  } catch {
    return null
  }
}

/**
 * Loader guard for the /login page. Signed-out users get the login form
 * (returns null). Already-signed-in users are redirected to where they were
 * headed (`?next=`, if same-origin) or to the app root, which routes them on
 * to the right place — the admin area for admins, their org workspace
 * otherwise (see the "/" route loader).
 */
export async function redirectIfAuthenticated(request: Request) {
  const data = await loaderQuery(useCurrentUserQuery, {})

  if (data.currentUser.pid == null) return null

  const next = resolveSafeNextPath(new URL(request.url).searchParams.get("next"))
  throw redirect(next ?? "/")
}
